import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { AccountStatus } from "../../../definitions/account-status";
import { UserRoles } from "../../../definitions/user-roles";
import {
	proposalBlockedReason,
	VendorProposalContext
} from "../../../shared/purchase-order.utils";
import { PurchaseOrderActions } from "../../../store/actions/purchase-order.actions";
import { VendorActions } from "../../../store/actions/vendor.actions";
import { AppState } from "../../../store/reducers";
import { MpCommonSelectors } from "../../../store/selectors/mp-common.selectors";
import { PurchaseOrderSelectors } from "../../../store/selectors/purchase-order.selectors";
import { SelectUser } from "../../../store/selectors/user.selectors";
import { VendorSelectors } from "../../../store/selectors/vendor.selectors";

/**
 * Blocks the add-proposal stepper whenever the order isn't accepting proposals
 * (bid window closed, order canceled) or this vendor can't submit one (inactive
 * account, missing the exchange role, no payout account).
 *
 * The button on the order detail page already reflects all of this, but the
 * route is reachable by deep link, and the stepper sources its order from router
 * navigation state — so a direct hit would otherwise render an empty form that
 * the API rejects on submit. Loading the order here also populates
 * `selectedOrder` for the component's deep-link fallback.
 *
 * Shares `proposalBlockedReason` with the button so the two can't drift apart.
 */
@Injectable({
	providedIn: "root"
})
export class AddProposalGuard {
	constructor(private store: Store<AppState>, private router: Router) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
		const orderId = route.paramMap.get("id");
		if (!orderId) {
			return of(this.router.parseUrl("/app/exchange"));
		}

		// clear first so a stale error from an earlier failed load can't resolve
		// this fetch immediately
		this.store.dispatch(PurchaseOrderActions.selectedOrder.clear());
		this.store.dispatch(PurchaseOrderActions.selectedOrder.get({ orderId }));
		// cache-first effect, so this is a no-op when the header already loaded it
		this.store.dispatch(VendorActions.account.get());

		return combineLatest([
			this.store.select(PurchaseOrderSelectors.selectedOrder),
			this.store.select(PurchaseOrderSelectors.error),
			this.store.select(VendorSelectors.account),
			this.store.select(SelectUser),
			this.store.select(MpCommonSelectors.userStatus),
			this.store.select(VendorSelectors.accountSettled)
		]).pipe(
			// The account slice must have settled too, not just the order: it emits
			// `{ loading: true }` synchronously, and reading funding sources out of
			// that bounces a vendor who does have a bank account.
			filter(
				([order, error, , , , accountSettled]) =>
					(order?.id === orderId || !!error) && accountSettled
			),
			take(1),
			map(([order, , account, user, userStatus]) => {
				const vendor: VendorProposalContext = {
					accountActive: AccountStatus.isActive(userStatus?.status),
					isExchangeAdmin: (user?.roles ?? []).some((role) =>
						UserRoles.vendorRange.exchange.includes(role)
					),
					fundingSourceCount: account?.fundingSources?.length ?? 0,
					hasActiveFundingSource: !!account?.vendor?.achActiveFundingSourceId
				};
				if (order?.id === orderId && proposalBlockedReason(order, vendor) === null) {
					return true;
				}
				return this.router.parseUrl(`/app/orders/${orderId}`);
			})
		);
	}
}
